import type { CampaignNavigationCategory } from '@btsis/contracts';
import { EnvironmentBanner, Iconify, Label, Logo, toast, useFocusReturn } from '@btsis/ui';
import Alert from '@mui/material/Alert';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useLocation, useNavigate } from 'react-router';
import { useDonationCampaignNavigation } from '@/features/donation/campaign-queries';
import { NotificationBell } from '@/features/notification/notification-bell';
import { operationsCampaignPath } from '@/features/operations/legacy-links';
import { useOperationCampaigns } from '@/features/operations/queries';
import { PseudonymRevealDialog } from '@/features/profile/pseudonym-reveal-dialog';
import { useHidePseudonyms } from '@/features/profile/queries';
import { ImpersonationBanner } from '@/features/user/impersonation-banner';
import { useAuth } from '@/lib/auth-context';
import { activeNavPaths } from '@/lib/nav-active';
import {
  isNavGroup,
  navLinksOf,
  OPERATION_CAMPAIGNS_GROUP,
  offeredNavSections,
} from '@/nav-config';

const NAV_WIDTH = 264;

const NAV_ITEM_SX = {
  borderRadius: 1.5,
  mb: 0.25,
  '&.active': { bgcolor: 'action.selected', color: 'primary.main', fontWeight: 700 },
} as const;

/** The saved view of the donation list a campaign's menu entry opens. */
const campaignNavPath = (campaignId: string) => `/donations?campaign=${campaignId}`;

/**
 * A parent with a second level beneath it, composed as the Minimal kit's
 * vertical `NavList` composes one: a toggle rather than a link, open while one
 * of its children is the current page, closed again once the reader leaves it,
 * and coloured — not selected — while a child is, so the child alone carries
 * the selected state.
 */
function NavGroupItem({
  title,
  icon,
  active,
  children,
}: {
  title: string;
  icon: string;
  active: boolean;
  children: ReactNode;
}) {
  const location = useLocation();
  const [open, setOpen] = useState(active);

  // biome-ignore lint/correctness/useExhaustiveDependencies: the kit's NavList closes an inactive group on every navigation, not only when `active` changes
  useEffect(() => {
    setOpen(active);
  }, [active, location.pathname, location.search]);

  return (
    <>
      <ListItemButton
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        sx={{
          ...NAV_ITEM_SX,
          ...(active && { color: 'primary.main', fontWeight: 700 }),
        }}
      >
        <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
          <Iconify icon={icon} width={22} />
        </ListItemIcon>
        <ListItemText
          primary={title}
          slotProps={{ primary: { fontSize: 14, fontWeight: 'inherit' } }}
        />
        <Iconify
          icon={open ? 'solar:alt-arrow-up-linear' : 'solar:alt-arrow-down-linear'}
          width={16}
        />
      </ListItemButton>
      <Collapse in={open} unmountOnExit>
        {children}
      </Collapse>
    </>
  );
}

function NavChildItem({
  to,
  title,
  active,
  onNavigate,
  count,
}: {
  to: string;
  title: string;
  active: boolean;
  onNavigate: () => void;
  /** What waits there, when the entry has a figure — an operations campaign's badge. */
  count?: number;
}) {
  return (
    <ListItemButton
      component={Link}
      to={to}
      className={active ? 'active' : undefined}
      aria-current={active ? 'page' : undefined}
      onClick={onNavigate}
      sx={{ ...NAV_ITEM_SX, pl: 5.5 }}
    >
      <ListItemText
        primary={title}
        slotProps={{ primary: { fontSize: 13, fontWeight: 'inherit' } }}
      />
      {count !== undefined && count > 0 && (
        <Label variant="soft" color="warning" sx={{ ml: 1 }}>
          {count}
        </Label>
      )}
    </ListItemButton>
  );
}

/**
 * One category from `donation.campaigns.navigation`, with the campaigns the caller may
 * actually collect for. Nothing here branches on which campaign it is: the
 * whole group is rendered from the row it came back as.
 *
 * The menu names a campaign, so it opens that campaign's whole donation list
 * rather than its active season or its management page — the parameters stay
 * reachable from Kampanyalar alone, docs/plans/10-operational-redesign.md §10.2.
 */
function CampaignCategoryNav({
  category,
  activePaths,
  onNavigate,
}: {
  category: CampaignNavigationCategory;
  activePaths: ReadonlySet<string>;
  onNavigate: () => void;
}) {
  return (
    <NavGroupItem
      title={category.name}
      icon={category.icon ?? 'solar:folder-bold-duotone'}
      active={category.campaigns.some((campaign) => activePaths.has(campaignNavPath(campaign.id)))}
    >
      {category.campaigns.map((campaign) => (
        <NavChildItem
          key={campaign.id}
          to={campaignNavPath(campaign.id)}
          title={campaign.name}
          active={activePaths.has(campaignNavPath(campaign.id))}
          onNavigate={onNavigate}
        />
      ))}
    </NavGroupItem>
  );
}

export function DashboardLayout() {
  const { t } = useTranslation();
  const { user, logout, can, staffMay, readsOrganizations } = useAuth();
  const impersonationExpired = Boolean(
    user?.impersonation && Date.parse(user.impersonation.expiresAt) <= Date.now(),
  );
  const impersonationNeedsFactor = Boolean(user?.impersonation && user.mustEnrolTwoFactor);
  const navigate = useNavigate();
  const location = useLocation();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [revealOpen, setRevealOpen] = useState(false);
  const hidePseudonyms = useHidePseudonyms();
  const [mobileOpen, setMobileOpen] = useState(false);
  const focusReturn = useFocusReturn(mobileOpen);
  const sections = offeredNavSections({ can, staffMay, readsOrganizations });
  // The static entries are the fallback: they render immediately and stay if
  // the menu query is still loading or fails, so navigation never disappears.
  // The menu leads to donation lists, so it is read behind `donation.list`,
  // never a campaign permission — docs/decisions/0088 — and only for a reader
  // whose role shows the entries (docs/decisions/0124).
  const campaignEntriesShown = sections.some((section) => section.campaignEntriesShown);
  const campaignNavigation = useDonationCampaignNavigation(campaignEntriesShown);
  // The operations campaigns are read behind `donation.fulfill`, which a staff
  // assignment derives, and their badges are the work waiting in the reader's
  // own reach — docs/decisions/0091. They are listed where the group's static
  // entry is offered, so they follow its menu key.
  const operationsGroupShown = sections.some(
    (section) =>
      section.operationsDriven &&
      section.items.some((item) => isNavGroup(item) && item.titleKey === OPERATION_CAMPAIGNS_GROUP),
  );
  const operationCampaigns = useOperationCampaigns(operationsGroupShown);
  const operationLinks = (operationsGroupShown ? (operationCampaigns.data?.items ?? []) : []).map(
    (campaign) => ({
      path: operationsCampaignPath(campaign.id),
      title: campaign.name,
      count: campaign.counts.waiting + campaign.counts.queued,
    }),
  );

  async function handleLogout() {
    setMenuAnchor(null);
    await logout();
    void navigate('/login', { replace: true });
  }

  const campaignCategories = campaignEntriesShown
    ? (campaignNavigation.data?.categories ?? [])
    : [];
  // A campaign's entry is a saved view of the donation list like any other, so
  // it competes for the highlight with the static links rather than beside them.
  const activePaths = activeNavPaths(
    [
      ...sections.flatMap((section) => navLinksOf(section.items).map((item) => item.path)),
      ...campaignCategories.flatMap((category) =>
        category.campaigns.map((campaign) => campaignNavPath(campaign.id)),
      ),
      ...operationLinks.map((link) => link.path),
    ],
    location,
  );
  const closeMobile = () => setMobileOpen(false);

  const nav = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 3, py: 3 }}>
        <Logo />
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, pb: 2 }}>
        {sections.map((section) => {
          const { items } = section;
          const categories = section.campaignEntriesShown ? campaignCategories : [];
          if (items.length === 0 && categories.length === 0) return null;
          return (
            <List
              key={section.titleKey}
              subheader={
                <ListSubheader
                  disableSticky
                  sx={{ bgcolor: 'transparent', fontSize: 11, fontWeight: 700, letterSpacing: 0.5 }}
                >
                  {t(section.titleKey).toLocaleUpperCase('tr-TR')}
                </ListSubheader>
              }
            >
              {items.map((item) => {
                if (!isNavGroup(item)) {
                  return (
                    <ListItemButton
                      key={item.path}
                      component={Link}
                      to={item.path}
                      className={activePaths.has(item.path) ? 'active' : undefined}
                      aria-current={activePaths.has(item.path) ? 'page' : undefined}
                      onClick={closeMobile}
                      sx={NAV_ITEM_SX}
                    >
                      <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
                        <Iconify icon={item.icon} width={22} />
                      </ListItemIcon>
                      <ListItemText
                        primary={t(item.titleKey)}
                        slotProps={{ primary: { fontSize: 14, fontWeight: 'inherit' } }}
                      />
                    </ListItemButton>
                  );
                }
                const dynamic =
                  section.operationsDriven && item.titleKey === OPERATION_CAMPAIGNS_GROUP
                    ? operationLinks
                    : [];
                return (
                  <NavGroupItem
                    key={item.titleKey}
                    title={t(item.titleKey)}
                    icon={item.icon}
                    active={
                      item.children.some((child) => activePaths.has(child.path)) ||
                      dynamic.some((link) => activePaths.has(link.path))
                    }
                  >
                    {item.children.map((child) => (
                      <NavChildItem
                        key={child.path}
                        to={child.path}
                        title={t(child.titleKey)}
                        active={activePaths.has(child.path)}
                        onNavigate={closeMobile}
                      />
                    ))}
                    {dynamic.map((link) => (
                      <NavChildItem
                        key={link.path}
                        to={link.path}
                        title={link.title}
                        count={link.count}
                        active={activePaths.has(link.path)}
                        onNavigate={closeMobile}
                      />
                    ))}
                  </NavGroupItem>
                );
              })}
              {categories.map((category) => (
                <CampaignCategoryNav
                  key={category.id}
                  category={category}
                  activePaths={activePaths}
                  onNavigate={closeMobile}
                />
              ))}
            </List>
          );
        })}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh', bgcolor: 'background.neutral' }}>
      <Box component="nav" sx={{ width: { lg: NAV_WIDTH }, flexShrink: { lg: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', lg: 'none' },
            '& .MuiDrawer-paper': { width: NAV_WIDTH, boxSizing: 'border-box' },
          }}
          {...focusReturn}
        >
          {nav}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', lg: 'block' },
            '& .MuiDrawer-paper': {
              width: NAV_WIDTH,
              boxSizing: 'border-box',
              borderRight: (theme) => `1px dashed ${theme.palette.divider}`,
            },
          }}
        >
          {nav}
        </Drawer>
      </Box>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <AppBar
          position="sticky"
          color="transparent"
          sx={{
            backdropFilter: 'blur(6px)',
            bgcolor: (theme) => `${theme.palette.background.default}cc`,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <EnvironmentBanner />
          <ImpersonationBanner />
          <Toolbar>
            <IconButton
              onClick={() => setMobileOpen(true)}
              sx={{ mr: 1, display: { lg: 'none' } }}
              aria-label={t('nav.dashboard')}
            >
              <Iconify icon="solar:hamburger-menu-broken" width={24} />
            </IconButton>
            <Box sx={{ flex: 1 }} />
            <Stack direction="row" spacing={1.5} alignItems="center">
              {user && (
                <Tooltip
                  title={t(
                    user.pseudonymsRevealed
                      ? 'pseudonym.chip.revealedHint'
                      : 'pseudonym.chip.maskedHint',
                  )}
                >
                  <Box data-testid="pseudonym-chip" component="span">
                    <Label color={user.pseudonymsRevealed ? 'warning' : 'default'}>
                      {t(
                        user.pseudonymsRevealed
                          ? 'pseudonym.chip.revealed'
                          : 'pseudonym.chip.masked',
                      )}
                    </Label>
                  </Box>
                </Tooltip>
              )}
              {!impersonationExpired && !impersonationNeedsFactor && <NotificationBell />}
              <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
                <Typography variant="subtitle2" sx={{ lineHeight: 1.2 }}>
                  {user?.name}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {user?.institution.name}
                </Typography>
              </Box>
              <IconButton onClick={(event) => setMenuAnchor(event.currentTarget)}>
                <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
                  {user?.name?.charAt(0).toLocaleUpperCase('tr-TR')}
                </Avatar>
              </IconButton>
            </Stack>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="subtitle2">{user?.name}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {user?.email}
                </Typography>
              </Box>
              <Divider />
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  void navigate('/profile');
                }}
              >
                <ListItemIcon>
                  <Iconify icon="solar:user-id-bold-duotone" />
                </ListItemIcon>
                {t('auth.profile')}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  void navigate('/my-assignments');
                }}
              >
                <ListItemIcon>
                  <Iconify icon="solar:clipboard-check-bold-duotone" />
                </ListItemIcon>
                {t('nav.myAssignments')}
              </MenuItem>
              {user?.canRevealPseudonyms && !user.impersonation && !user.pseudonymsRevealed && (
                <MenuItem
                  disabled={!user.twoFactorEnabled}
                  onClick={() => {
                    setMenuAnchor(null);
                    setRevealOpen(true);
                  }}
                >
                  <ListItemIcon>
                    <Iconify icon="solar:eye-bold" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('pseudonym.menu.reveal')}
                    secondary={
                      user.twoFactorEnabled ? undefined : t('pseudonym.menu.needsTwoFactor')
                    }
                  />
                </MenuItem>
              )}
              {user?.canRevealPseudonyms && !user.twoFactorEnabled && !user.impersonation && (
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null);
                    void navigate('/profile');
                  }}
                >
                  <ListItemIcon>
                    <Iconify icon="solar:lock-keyhole-bold-duotone" />
                  </ListItemIcon>
                  {t('profile.twoFactor.title')}
                </MenuItem>
              )}
              {user?.pseudonymsRevealed && (
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null);
                    hidePseudonyms.mutate(undefined, {
                      onSuccess: () => toast.success(t('pseudonym.dialog.hidden')),
                    });
                  }}
                >
                  <ListItemIcon>
                    <Iconify icon="solar:eye-closed-bold" />
                  </ListItemIcon>
                  {t('pseudonym.menu.hide')}
                </MenuItem>
              )}
              <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                <ListItemIcon sx={{ color: 'inherit' }}>
                  <Iconify icon="solar:logout-3-bold-duotone" />
                </ListItemIcon>
                {t('auth.logout')}
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        <PseudonymRevealDialog open={revealOpen} onClose={() => setRevealOpen(false)} />

        <Box component="main" sx={{ flex: 1, p: { xs: 2, sm: 3 } }}>
          {impersonationExpired ? (
            <Alert severity="warning">{t('impersonation.expired')}</Alert>
          ) : impersonationNeedsFactor ? (
            <Alert severity="warning">{t('error.TWO_FACTOR_REQUIRED')}</Alert>
          ) : (
            <Outlet />
          )}
        </Box>
      </Box>
    </Box>
  );
}
